import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { DocumentService } from "../services/DocumentService";
import { DocLinks } from "../types/form";

type Ctx = {
    links: DocLinks;
    loading: boolean;
    error: string | null;
};

const DEFAULT_LINKS: DocLinks = {
    regulations: "/regulations",
    oferta: "/oferta",               // важно: пусто, чтобы не ловить редирект-циклы на /oferta
    privacy: "/privacy_policy",
};

const STORAGE_KEY = "docLinksCache";

const DocLinksContext = createContext<Ctx>({
    links: DEFAULT_LINKS,
    loading: false,
    error: null,
});

export function DocLinksProvider({ children }: { children: ReactNode }) {
    // 1) Сразу отдаём дефолт/кэш — чтобы UI был моментально
    const cached = safeReadCache();
    const [links, setLinks] = useState<DocLinks>(cached ?? DEFAULT_LINKS);
    const [loading, setLoading] = useState(false); // мгновенно без лоадера
    const [error, setError] = useState<string | null>(null);

    // защита от двойного вызова эффекта в React 18 StrictMode (dev)
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return; // не запускать повторно
        startedRef.current = true;

        const controller = new AbortController();
        const svc = new DocumentService();

        (async () => {
            try {
                setLoading(true);

                // 2) таймаут на запрос (например, 5 сек.)
                const timeoutId = setTimeout(() => controller.abort(), 5000);

                // 3) грузим (параметр true — как у тебя)
                const data = await svc.getAll();

                clearTimeout(timeoutId);

                const next: DocLinks = {
                    regulations: data?.regulations || DEFAULT_LINKS.regulations,
                    oferta: data?.oferta || DEFAULT_LINKS.oferta,
                    privacy: data?.privacy || DEFAULT_LINKS.privacy,
                };

                setLinks(next);
                safeWriteCache(next);
                setError(null);
            } catch (e: any) {
                if (e?.name === "AbortError") {
                    // тихо игнорим — пользователь и так видит кэш/дефолт
                } else {
                    setError(e?.message ?? "Ошибка загрузки ссылок");
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, []);

    return (
        <DocLinksContext.Provider value={{ links, loading, error }}>
            {children}
        </DocLinksContext.Provider>
    );
}

/** Хук всегда возвращает строки (без undefined), чтобы не было TS2345 */
export function useDocumentLinks(): DocLinks {
    const { links } = useContext(DocLinksContext);
    return links;
}

// если где-то нужен loading/error
export function useDocLinksContext() {
    return useContext(DocLinksContext);
}

/* ---------- helpers ---------- */

function safeReadCache(): DocLinks | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // лёгкая валидация
        if (
            typeof parsed?.regulations === "string" &&
            typeof parsed?.oferta === "string" &&
            typeof parsed?.privacy === "string"
        ) {
            return parsed as DocLinks;
        }
        return null;
    } catch {
        return null;
    }
}

function safeWriteCache(value: DocLinks) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
        /* ignore */
    }
}